/**
 * ModalModelViewer Component
 *
 * A simplified 3D model viewer specifically designed for modal dialogs.
 * Provides basic model viewing without the complex measurement tools.
 */

import React, { Suspense, useRef, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { Vector3 } from "three";
import { Canvas } from "@react-three/fiber";
import { Html, OrbitControls } from "@react-three/drei";
import { ModelFromUrl } from "../../../model-viewer/utils";
import { shoeStringArea } from "../../../model-viewer/utils/area-formulas";
import { PointLineGroup } from "../../../model-viewer/geometry";
import { urlExpired } from "../../../../utils/url-expired";

export interface ModalModelViewerProps {
  /** URL to the 3D model file (PLY format) */
  modelUrl?: string;
  /** Unique identifier for the patient */
  patientId: string;
  /** Whether area measurement tool is active */
  showAreaTool?: boolean;
  /** Current measurement points */
  points?: Vector3[];
  /** Callback when points change */
  onPointsChange?: (points: Vector3[]) => void;
  /** Callback when distance changes */
  onDistanceChange?: (distance: number) => void;
  /** Callback when angle changes */
  onAngleChange?: (angle: number) => void;
  /** Callback when area changes */
  onAreaChange?: (area: number) => void;
  /** Callback when perimeter changes */
  onPerimeterChange?: (perimeter: number) => void;
  /** Initial camera position */
  cameraPosition?: Vector3;
  /** Initial camera target */
  cameraTarget?: Vector3;
  /** Callback when camera position changes */
  onCameraChange?: (position: Vector3, target: Vector3) => void;
}

export const ModalModelViewer: React.FC<ModalModelViewerProps> = ({
  modelUrl,
  patientId,
  showAreaTool,
  points,
  onPointsChange,
  onDistanceChange,
  onAngleChange,
  onAreaChange,
  onPerimeterChange,
  cameraPosition,
  cameraTarget,
  onCameraChange,
}) => {
  const modelPosition = new Vector3(0, 0, 0);
  const target = cameraTarget || new Vector3(0, 0, 0);
  const orbitControlsRef = useRef<any>(null);
  
  const expired = modelUrl ? urlExpired(modelUrl) : false;

  // Set initial camera position if provided
  useEffect(() => {
    if (orbitControlsRef.current && cameraPosition) {
      orbitControlsRef.current.object.position.copy(cameraPosition);
      if (cameraTarget) {
        orbitControlsRef.current.target.copy(cameraTarget);
      }
      orbitControlsRef.current.update();
    }
  }, [cameraPosition, cameraTarget, modelUrl]);

  // Track camera changes
  const handleCameraChange = () => {
    if (orbitControlsRef.current && onCameraChange) {
      const position = orbitControlsRef.current.object.position.clone();
      const target = orbitControlsRef.current.target.clone();
      onCameraChange(position, target);
    }
  };

  const pointsChanged = (event: any) => {
    if (!onPointsChange) return;

    const point = event.point;
    const currentPoints = points || [];

    // Distance tool: replace points after 2, Area tool: accumulate points
    const newPoints = currentPoints.length === 2 && !showAreaTool
      ? [point]
      : [...currentPoints, point];

    // Need at least 2 points for any calculations
    if (newPoints.length < 2) {
      onPointsChange(newPoints);
      if (onDistanceChange) onDistanceChange(-1);
      return;
    }

    // Update points
    onPointsChange(newPoints);

    // Calculate distance
    if (newPoints.length >= 2 && onDistanceChange) {
      const distance = newPoints[0].distanceTo(newPoints[1]);
      onDistanceChange(distance);
    }

    // Calculate angle
    if (newPoints.length >= 2 && onAngleChange) {
      const angle = (newPoints[0].angleTo(newPoints[1]) * 180) / Math.PI;
      onAngleChange(angle);
    }

    // Calculate area and perimeter for area tool
    if (showAreaTool && newPoints.length >= 3) {
      if (onAreaChange && onPerimeterChange) {
        try {
          const { area, perimeter, selfIntersecting } = shoeStringArea(newPoints, true);
          
          if (!selfIntersecting) {
            onAreaChange(area);
            onPerimeterChange(perimeter);
          }
        } catch (error) {
          console.warn("Area calculation failed:", error);
          // Fallback to simple perimeter calculation
          let perimeter = 0;
          for (let i = 0; i < newPoints.length; i++) {
            const current = newPoints[i];
            const next = newPoints[(i + 1) % newPoints.length];
            perimeter += current.distanceTo(next);
          }
          onPerimeterChange(perimeter);
          onAreaChange(0);
        }
      }
    }
  };

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        backgroundColor: "#2a2a2a",
      }}
    >
      {/* 3D Canvas Container */}
      <Canvas
        style={{
          height: "100%",
          width: "100%",
        }}
      >
        {modelUrl && (
          <Suspense
            fallback={
              <Html position={[0, 0, 0]}>
                <CircularProgress sx={{ color: "white" }} />
              </Html>
            }
          >
            {/* Main 3D model component */}
            <ModelFromUrl
              url={modelUrl}
              gridPoints3D={[]}
              position={modelPosition}
              expired={expired}
              planeNormal={new Vector3(0, 1, 0)}
              onClick={pointsChanged}
            />

            {/* Measurement visualization */}
            {points && points.length > 0 && (
              <PointLineGroup
                points={points}
                visible={true}
                stringLineColor="#2563eb"
              />
            )}

            {/* Lighting */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <directionalLight position={[-10, -10, -5]} intensity={0.5} />

            {/* Camera controls */}
            <OrbitControls
              ref={orbitControlsRef}
              target={target}
              enablePan={true}
              enableZoom={true}
              enableRotate={true}
              maxDistance={50}
              minDistance={1}
              onChange={handleCameraChange}
            />
          </Suspense>
        )}
      </Canvas>



      {/* Error state */}
      {expired && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            fontSize: "18px",
            zIndex: 100,
          }}
        >
          Model URL has expired
        </div>
      )}
    </div>
  );
};

export default ModalModelViewer;